import { Vector3 } from 'three';
import type HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { cleanupSliversAround } from './seamSliverCleanup';

/**
 * Makes the gum's socket rim and the tooth's border loop the SAME polyline,
 * vertex for vertex.
 *
 * The snap step puts every gum border vertex it can onto a tooth border
 * vertex, but the tooth rim is denser: a gum rim edge is a chord across two
 * or three tooth border vertices, and gum vertices with no tooth vertex in
 * range land on border SEGMENTS instead. Every one of those unpaired vertices
 * reaches the export as a T-junction, and the export's splitter resolves
 * T-junctions by cutting edges wherever the opposing vertex happens to lie —
 * arbitrarily close to an existing vertex, which is where the sliver
 * triangles at the gum line come from.
 *
 * This pass removes the mismatch at the source instead:
 *   1. gum vertices sitting on a segment slide onto the nearest UNPAIRED
 *      tooth border vertex (or collapse into a paired gum neighbour when
 *      both endpoints are taken),
 *   2. tooth border vertices with no gum partner collapse into a paired
 *      neighbour along the border — but only SHORT runs; a long unpaired run
 *      is an interproximal window or a scan hole where no gum runs alongside,
 *      and destroying it would eat the wall-fill arcs.
 *
 * End state: wherever gum and tooth actually meet, both rims share an
 * identical vertex sequence at identical positions, so the export fusion
 * degenerates to an exact weld with nothing left to split.
 */
export interface SeamSnapInfo {
  toothVertex: Vertex;
  toothIndex: number;
  toothMesh: HalfEdgeMesh;
}

export interface SeamRimEqualizationStats {
  exactPairs: number;
  gumSlid: number;
  gumCollapsed: number;
  gumStuck: number;
  toothCollapsed: number;
  toothStuck: number;
  runsKept: number;
  sliversWelded: number;
  sliversCollapsed: number;
}

export interface SeamRimEqualizationOptions {
  /** Longest unpaired tooth-border run (in vertices) still collapsed away. */
  maxRunLength?: number;
  /** Longest unpaired run (in mm along the border) still collapsed away. */
  maxRunSpanMm?: number;
}

const EXACT_EPSILON = 1e-3;

export function equalizeSeamRims(
  gum: HalfEdgeMesh,
  teeth: HalfEdgeMesh[],
  gumToSnap: Map<Vertex, SeamSnapInfo>,
  options?: SeamRimEqualizationOptions,
): SeamRimEqualizationStats {
  const stats: SeamRimEqualizationStats = {
    exactPairs: 0,
    gumSlid: 0,
    gumCollapsed: 0,
    gumStuck: 0,
    toothCollapsed: 0,
    toothStuck: 0,
    runsKept: 0,
    sliversWelded: 0,
    sliversCollapsed: 0,
  };
  const maxRunLength = options?.maxRunLength ?? 3;

  gum.updateWorldMatrix(true, false);
  teeth.forEach((tooth) => {
    tooth.updateWorldMatrix(true, false);
    tooth.calculateBorderVertices();
  });

  const gumWorld = (v: Vertex) => gum.localToWorld(v.position.clone());
  const toothWorld = (tooth: HalfEdgeMesh, v: Vertex) => tooth.localToWorld(v.position.clone());

  // ---- Phase 1: exact pairs + resolution of segment-snapped gum vertices ----
  // partner: per tooth, which tooth border vertex is claimed by which gum vertex.
  const partner = new Map<HalfEdgeMesh, Map<Vertex, Vertex>>();
  const partnersOf = (tooth: HalfEdgeMesh): Map<Vertex, Vertex> => {
    let map = partner.get(tooth);
    if (!map) {
      map = new Map();
      partner.set(tooth, map);
    }
    return map;
  };
  const claim = (info: SeamSnapInfo, toothVertex: Vertex, gumVertex: Vertex) => {
    partnersOf(info.toothMesh).set(toothVertex, gumVertex);
  };

  const segmentSnapped: Array<[Vertex, SeamSnapInfo]> = [];
  for (const [gumVertex, info] of gumToSnap) {
    const distance = gumWorld(gumVertex).distanceTo(toothWorld(info.toothMesh, info.toothVertex));
    if (distance <= EXACT_EPSILON) {
      claim(info, info.toothVertex, gumVertex);
      stats.exactPairs++;
    } else {
      segmentSnapped.push([gumVertex, info]);
    }
  }

  for (const [gumVertex, info] of segmentSnapped) {
    const taken = partnersOf(info.toothMesh);
    // Candidates: the stored nearest endpoint and its border neighbours —
    // the vertex sits ON one of these vertices' border segments.
    const candidates = [info.toothVertex, ...info.toothVertex.getAdjacenceBoundaryVertices()];
    let best: Vertex | null = null;
    let bestDistance = Infinity;
    const gumPos = gumWorld(gumVertex);
    for (const candidate of candidates) {
      if (taken.has(candidate)) continue;
      const distance = gumPos.distanceTo(toothWorld(info.toothMesh, candidate));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
    if (best) {
      gumVertex.position.copy(gum.worldToLocal(toothWorld(info.toothMesh, best)));
      info.toothVertex = best;
      claim(info, best, gumVertex);
      stats.gumSlid++;
      stats.exactPairs++;
      continue;
    }
    // Both endpoints taken — this gum vertex is redundant density; merge it
    // into a border neighbour that already holds an exact pair.
    let collapsed = false;
    for (const neighbour of gumVertex.getAdjacenceBoundaryVertices()) {
      if (!gumToSnap.has(neighbour)) continue;
      if (gum.halfEdges.edgeCollapse(gumVertex, neighbour, false)) {
        gumToSnap.delete(gumVertex);
        stats.gumCollapsed++;
        collapsed = true;
        break;
      }
    }
    if (!collapsed) stats.gumStuck++;
  }

  // ---- Phase 2: collapse short unpaired runs on each tooth border ----
  const spanScratch = new Vector3();
  for (const tooth of teeth) {
    const taken = partnersOf(tooth);
    if (taken.size === 0) continue;
    tooth.calculateBorderVertices();
    const meanEdge = toothMeanBorderEdge(tooth);
    const maxRunSpanMm = options?.maxRunSpanMm ?? 4 * meanEdge;

    for (const loop of tooth.getBorderLoops()) {
      const n = loop.length;
      if (n < 4) continue;
      const paired = loop.map((v) => taken.has(v));
      if (!paired.some(Boolean)) continue;

      // Walk maximal unpaired runs between paired ends.
      let start = paired.indexOf(true);
      let i = start;
      do {
        const runStartAnchor = loop[i];
        let j = (i + 1) % n;
        const run: Vertex[] = [];
        while (!paired[j]) {
          run.push(loop[j]);
          j = (j + 1) % n;
          if (j === i) break; // degenerate: single paired vertex
        }
        if (run.length > 0 && paired[j]) {
          let span = 0;
          let previous = runStartAnchor;
          for (const v of [...run, loop[j]]) {
            span += spanScratch.subVectors(v.position, previous.position).length();
            previous = v;
          }
          if (run.length <= maxRunLength && span <= maxRunSpanMm) {
            // Collapse from both ends inward so every removed vertex merges
            // into a PAIRED survivor (targets keep their position).
            let lo = 0;
            let hi = run.length - 1;
            let loAnchor = runStartAnchor;
            let hiAnchor = loop[j];
            while (lo <= hi) {
              if (tooth.halfEdges.edgeCollapse(run[lo], loAnchor, false)) {
                stats.toothCollapsed++;
                lo++;
                continue;
              }
              if (lo < hi && tooth.halfEdges.edgeCollapse(run[hi], hiAnchor, false)) {
                stats.toothCollapsed++;
                hi--;
                continue;
              }
              // Neither end collapses (link condition / flip guard) — leave
              // the rest of this run in place rather than loop forever.
              stats.toothStuck += hi - lo + 1;
              break;
            }
          } else {
            stats.runsKept++;
          }
        }
        i = j;
      } while (i !== start && paired[i]);
    }
    tooth.calculateBorderVertices();
  }

  // ---- Phase 3: sliver cleanup around the seam ----
  // Snapping and collapsing can leave a vertex lying almost ON an edge of a
  // neighbouring triangle (a fan of paper-thin triangles). Weld those into
  // the edge they hug; seam polylines stay pinned.
  gum.calculateBorderVertices();
  const gumCleanup = cleanupSliversAround(
    gum,
    gumToSnap.keys(),
    new Set(gum.getBorderVertices()),
    'gum seam',
  );
  stats.sliversWelded += gumCleanup.welded;
  stats.sliversCollapsed += gumCleanup.collapsed;
  for (const tooth of teeth) {
    const taken = partnersOf(tooth);
    if (taken.size === 0) continue;
    tooth.calculateBorderVertices();
    const toothCleanup = cleanupSliversAround(
      tooth,
      taken.keys(),
      new Set(tooth.getBorderVertices()),
      'tooth seam',
    );
    stats.sliversWelded += toothCleanup.welded;
    stats.sliversCollapsed += toothCleanup.collapsed;
    tooth.calculateBorderVertices();
  }

  gum.calculateBorderVertices();
  return stats;
}

function toothMeanBorderEdge(tooth: HalfEdgeMesh): number {
  let total = 0;
  let count = 0;
  for (const loop of tooth.getBorderLoops()) {
    for (let i = 0; i < loop.length; i++) {
      total += loop[i].position.distanceTo(loop[(i + 1) % loop.length].position);
      count++;
    }
  }
  return count > 0 ? total / count : 0.25;
}
